
import React, { useState } from "react";

import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid2 from '@mui/material/Grid2';

import { Toolbar, TextField, Button, Snackbar, Alert, Typography } from "@mui/material";
import { InvokeAPI } from "../apis/API";
import { getUserMgmtBase } from "../config/apiBase";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useNavigate } from "react-router-dom";
import AsyncButton from "../components/common/AsyncButton";
import { useAsyncAction } from "../components/common/useAsyncAction";
import AuthPageBackground from "../components/auth/AuthPageBackground";


const ForgotPassword = () => {
  const navigate = useNavigate();

  const [value, setValue] = React.useState("");
  const [emailError, setEmailError] = useState("");
  const { run, loading: submitting } = useAsyncAction();

  const handleChange = (event) => {
    event.preventDefault();
    setValue(event.target.value)
    if (emailError) {
      setEmailError("");
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    const email = (value || "").trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      setEmailError("Email is required");
      return;
    }

    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    await run(async () => {
      const uinfo = await InvokeAPI(`${getUserMgmtBase()}/v1/user/password/forgot`,
        {
          "EmailId": email,
        },
        'POST',
        {},
        false)
      if (uinfo.notfound) {
        setState({ open: true, severity: "error", message: "This email id is not registered with us." })
        setValue("")
      } else if (uinfo.rateLimitExceeded || (uinfo.errorMessage && uinfo.errorMessage.includes("reset password request limit reached"))) {
        setState({ open: true, severity: "error", message: "Reset password request limit reached. Please try again after 24 hours." })
        setValue("")
      } else if (uinfo.iserror) {
        setState({ open: true, severity: "error", message: "Internal error please try again or send email to administrator." })
        setValue("")
      } else {
        setState({ open: true, severity: "success", message: "Reset password link sent to your email address if you are registered with us." })
        setValue("")
      }
    });
  }

  const [state, setState] = useState({ open: false, message: "", severity: "" })

  const handleClose = () => {
    setState({ ...state, open: false });
  };


  return (
    <AuthPageBackground>
    <Grid2 spacing={2}>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={state.open}
        onClose={handleClose}
        key={state.message}
        autoHideDuration={6000}
        TransitionProps={{ timeout: 300 }}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          top: { xs: 72, sm: 80 },
        }}
      >
        <Alert 
          variant="filled" 
          severity={state.severity}
          sx={{
            animation: 'slideUp 0.3s ease-out',
            '@keyframes slideUp': {
              from: {
                opacity: 0,
                transform: 'translateY(-20px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          {state.message}
        </Alert>
      </Snackbar>
      
      <Toolbar></Toolbar>
      <Grid2 display="flex" flexDirection="column" justifyContent="center" alignItems="center" size="grow" sx={{ marginTop: '50px', px: 2 }}>
        <Box sx={{ maxWidth: 450, width: '100%' }}>
          {/* Header Section */}
          <Box 
            sx={{ 
              display: 'flex',
              backgroundColor: 'primary.main',
              borderRadius: '16px 16px 0 0',
              padding: '16px 24px',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.contrastText',
              }}
            >
              Forgot Password
            </Typography>
          </Box>
          
          {/* Content Card */}
          <Paper 
            elevation={0}
            sx={{
              borderRadius: '0 0 16px 16px',
              boxShadow: (theme) => theme.palette.mode === 'dark'
                ? '0 12px 40px rgba(0, 188, 212, 0.35), 0 0 0 1px rgba(0, 188, 212, 0.25)'
                : '0 12px 40px rgba(0, 151, 167, 0.15)',
              width: '100%',
              overflow: 'hidden',
              backgroundColor: 'background.paper',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: 'fadeIn 0.5s ease-in',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(20px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Box sx={{ p: 3 }}>
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center"
                sx={{ mb: 3 }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
                  Enter your email address to send the reset link.
                </Typography>
              </Box>
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                <TextField
                  label="Email"
                  variant="outlined"
                  name="email"
                  value={value}
                  onChange={handleChange}
                  disabled={submitting}
                  fullWidth
                  required
                  autoComplete="email"
                  error={!!emailError}
                  helperText={emailError}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                      },
                    },
                  }}
                />
              </Box>
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                <AsyncButton 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  onClick={handleForgotPasswordSubmit}
                  loading={submitting}
                  fullWidth
                  sx={{ 
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: (theme) => theme.shadows[8],
                    },
                  }}
                >
                  Reset Password
                </AsyncButton>
              </Box>
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                <Button 
                  sx={{ 
                    textTransform: "initial",
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateX(-4px)',
                    },
                  }} 
                  type="link" 
                  color="primary" 
                  onClick={(e) => { e.preventDefault(); navigate("/app") }}
                >
                  <KeyboardArrowLeftIcon sx={{ mr: 0.5 }} />Back to Login page
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
        <br />
      </Grid2>
    </Grid2>
    </AuthPageBackground>

  );
};

export default ForgotPassword;
